import { useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Plus, Check, Scales } from "@phosphor-icons/react";
import { metricOptions, API } from "./constants";
import { formatDateIT } from "@/lib/formatDate";

export function MeasurementsView({ measurements, token, onAdded }) {
  const [chartMetric, setChartMetric] = useState("weight_kg");
  const [showForm, setShowForm] = useState(false);
  const [weight, setWeight] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const currentMetric = metricOptions.find((m) => m.key === chartMetric);
  const chartData = measurements
    .map((m) => ({ date: m.date, value: m[chartMetric] }))
    .filter((d) => d.value != null);

  const latest = measurements.length > 0 ? measurements[measurements.length - 1] : null;

  const handleSaveWeight = async () => {
    if (!weight) return;
    setSaving(true);
    try {
      const payload = { weight_kg: parseFloat(weight), date, notes };
      const resp = await fetch(`${API}/shared/${token}/measurements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        setSaved(true);
        setTimeout(() => {
          setSaved(false);
          setShowForm(false);
          setWeight("");
          setNotes("");
          setDate(new Date().toISOString().split("T")[0]);
        }, 900);
        if (onAdded) onAdded();
      }
    } catch (err) { console.debug("[MeasurementsView]", err); } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Quick-add peso */}
      <div className="bg-[#171717] border border-[#262626] rounded-xl overflow-hidden" data-testid="shared-weight-card">
        {!showForm ? (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="w-full flex items-center gap-3 p-4 text-left active:bg-[#1F1F1F] transition-colors"
            data-testid="shared-weight-add"
          >
            <div className="w-10 h-10 rounded-xl bg-[#007AFF]/15 border border-[#007AFF]/30 flex items-center justify-center flex-shrink-0">
              <Scales size={18} weight="fill" className="text-[#007AFF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Aggiungi peso</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">Registra il peso di oggi per tenerlo tracciato</p>
            </div>
            <Plus size={16} weight="bold" className="text-zinc-400" />
          </button>
        ) : (
          <div className="p-4 space-y-3" data-testid="shared-weight-form">
            <div className="flex items-center gap-2">
              <Scales size={16} weight="fill" className="text-[#007AFF]" />
              <span className="text-xs font-bold uppercase tracking-wider text-white">Nuovo peso</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Peso (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="72.5"
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-base text-white outline-none focus:border-[#007AFF]"
                  data-testid="shared-weight-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Data</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-base text-white outline-none focus:border-[#007AFF]"
                  data-testid="shared-weight-date"
                />
              </div>
            </div>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note (opzionale)"
              className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#007AFF] placeholder:text-zinc-600"
              data-testid="shared-weight-notes"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                disabled={saving}
                className="px-3 py-2 rounded-lg bg-[#1A1A1A] border border-[#262626] text-zinc-400 text-xs font-medium"
                data-testid="shared-weight-cancel"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleSaveWeight}
                disabled={!weight || saving}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  saved
                    ? "bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30"
                    : "bg-[#007AFF] text-white disabled:opacity-50"
                }`}
                data-testid="shared-weight-save"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : saved ? (
                  <><Check size={14} weight="bold" /> Salvato</>
                ) : (
                  <>Salva</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {latest && (
        <div className="grid grid-cols-2 gap-2">
          {metricOptions.slice(0, 4).map((opt) => {
            const val = latest[opt.key];
            if (!val) return null;
            return (
              <div key={opt.key} className="bg-[#171717] border border-[#262626] rounded-xl p-3.5">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">{opt.label}</p>
                <p className="text-xl font-bold mt-1" style={{ color: opt.color }}>{val}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-[#171717] border border-[#262626] rounded-xl p-4">
        <div className="flex gap-1.5 flex-wrap mb-4">
          {metricOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setChartMetric(opt.key)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
                chartMetric === opt.key
                  ? "bg-[#007AFF] text-white"
                  : "bg-[#0A0A0A] text-zinc-500 border border-[#262626]"
              }`}
              data-testid={`shared-metric-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="date" stroke="#555" tick={{ fontSize: 10 }} />
              <YAxis stroke="#555" tick={{ fontSize: 10 }} width={40} />
              <Tooltip
                contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#888' }}
                itemStyle={{ color: currentMetric?.color }}
              />
              <Line
                type="monotone"
                dataKey="value"
                name={currentMetric?.label}
                stroke={currentMetric?.color}
                strokeWidth={2}
                dot={{ fill: currentMetric?.color, r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center py-8 text-zinc-600 text-xs">
            {measurements.length === 0 ? "Nessuna misurazione" : "Servono almeno 2 misurazioni per il grafico"}
          </p>
        )}
      </div>

      {measurements.length > 0 && (
        <div className="bg-[#171717] border border-[#262626] rounded-xl p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Storico</h3>
          <div className="space-y-2">
            {[...measurements].reverse().map((m) => (
              <div key={m.id} className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1E1E1E]">
                <p className="text-xs font-medium text-white mb-1">{formatDateIT(m.date)}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-400">
                  {m.weight_kg && <span>Peso: <span className="text-[#007AFF] font-medium">{m.weight_kg}kg</span></span>}
                  {m.body_fat_pct && <span>Grasso: <span className="text-[#D4FF00] font-medium">{m.body_fat_pct}%</span></span>}
                  {m.chest_cm && <span>Petto: {m.chest_cm}cm</span>}
                  {m.waist_cm && <span>Vita: {m.waist_cm}cm</span>}
                  {m.biceps_cm && <span>Bicipiti: {m.biceps_cm}cm</span>}
                  {m.thighs_cm && <span>Cosce: {m.thighs_cm}cm</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
